import axios from "axios";
import { CreateInvitationInterface } from "./interface/InvitationInterface";
import { FirstWordInput } from "./interface/FirstWordInterface";
import { announceProRequired, FeatureKey } from "./common/plan";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosClient.interceptors.response.use(
  (response) => response,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("little-feather-token");
      window.location.reload();
    }
    /* 402 means the baby is on the free plan and this write needs Pro. Reads
       are never refused, so this only ever follows something the user did. */
    if (err.response?.status === 402) {
      const code = err.response.data?.code;
      if (code === "pro_feature" || code === "entitlement_required") {
        announceProRequired(
          err.response.data?.feature as FeatureKey | undefined
        );
      }
    }
    return Promise.reject(err);
  }
);

const defaultOptions = () => {
  const accessToken = localStorage.getItem("little-feather-token");
  if (accessToken) {
    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
  }
  return {};
};

const client = {
  get: (url: string, options = {}) =>
    axiosClient.get(url, { ...defaultOptions(), ...options }),
  post: (url: string, data: any, options = {}) =>
    axiosClient.post(url, data, { ...defaultOptions(), ...options }),
  put: (url: string, data: any, options = {}) =>
    axiosClient.put(url, data, { ...defaultOptions(), ...options }),
  delete: (url: string, options = {}) =>
    axiosClient.delete(url, { ...defaultOptions(), ...options }),
  patch: (url: string, data: any, options = {}) =>
    axiosClient.patch(url, data, { ...defaultOptions(), ...options }),
};
const registerUserOptions = (accessToken: string) => {
  if (accessToken) {
    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
  }
  return {};
};
const registerUserClient = {
  post: (url: string, data: any, options: any = {}) =>
    axiosClient.post(url, data, {
      ...registerUserOptions(data.accessToken ?? ""),
      ...options,
    }),
};

// const options = () => {
//   // eslint-disable-line no-unused-vars, @typescript-eslint/no-unused-vars
//   const accessToken = localStorage.getItem('firebase-token')
//   if (accessToken) {
//     return {
//       headers: {
//         Authorization: `Bearer ${accessToken}`,
//       },
//     }
//   }
//   return {}
// }

//Users
export const addUser = (user: any) => registerUserClient.post("/user", user);
export const authenticateUser = () => client.get("/user/auth");
export const deleteAccount = () => client.delete(`/user`);

/* Called straight after a native purchase or restore. The server asks the store
   directly rather than trusting anything the client says, so this only tells it
   when to look. Without it the entitlement reaches us solely via the webhook,
   which runs seconds to a minute behind — far too slow to hold a paywall open. */
export const syncNativePurchase = () => client.post("/payment/native/sync", {});

/* Whether the store subscription on this device belongs to the signed-in
   account. Deliberately never says *which* account owns it — naming it would
   disclose one customer's account to another — so the UI can only offer to move
   it here, not identify who is losing it. */
export const getSubscriptionOwnership = () =>
  client.get("/payment/subscription/ownership");

/* Moves the store subscription to the signed-in account. The destination is the
   caller's own token server-side, so there is nothing to pass. */
export const transferSubscriptionOwnership = () =>
  client.post("/payment/subscription/transfer", {});

/* Whether THIS baby is paid for — a different question from whether the signed-in
   account has paid. The parent buys the journal and invited family contribute to
   it, so asking the caller's own flag drew locks over babies somebody else was
   paying for. Returns 403 if the caller is not on the baby. */
export const getBabyEntitlement = (babyId: string) =>
  client.get(`/payment/entitlement/baby/${babyId}`);

//Baby
export const addBaby = (baby: any) => client.post("/baby", baby);
export const getBabyById = (id: any) => client.get(`/baby/${id}`);
export const getAllBabies = () => client.get("/baby");
export const getLatestAddedBaby = () => client.get("/baby/latest");
export const updateBabyData = (id: any, baby: any) =>
  client.patch(`/baby/${id}`, baby);
export const addBabyMeasurement = (id: any, measurements: any) =>
  client.post(`/baby/${id}/Measurement`, measurements);

//Post
export const addPost = (post: any) => client.post("/post", post);
export const getAllPostsByBabyId = (babyId: any) =>
  client.get(`/post/${babyId}`);

//Milestones

//Family tree
export const getFamilyTreeByBaby = (babyId: any) =>
  client.get(`/milestone/${babyId}/family-tree`);
export const addFamilyTreeMember = (familyMember: any) =>
  client.post(`/milestone/family-tree`, familyMember);
export const updateFamilyTreeMember = (babyId: any, familyMember: any) =>
  client.patch(`/milestone/${babyId}/family-tree`, familyMember);
export const deleteFamilyTreeMember = (babyId: any, position: any) =>
  client.delete(`/milestone/${babyId}/family-tree/${position}`);

//month by month Milestone
export const getMonthlyDiaryByBaby = (babyId: any, year: any) => {
  return client.get(`/milestone/${babyId}/month-by-month?year=${year}`);
};
export const addMonthlyDiary = (monthlyDiary: any) =>
  client.post(`/milestone/month-by-month`, monthlyDiary);
export const updateMonthlyDiary = (monthlyDiary: any) =>
  client.patch(
    `/milestone/${monthlyDiary.baby_id}/month-by-month`,
    monthlyDiary
  );
export const getFirstYearByBaby = (babyId: any, year: any) =>
  client.get(`/milestone/${babyId}/year-milestone?year=${year}`);
export const addFirstYear = (firstYear: any) =>
  client.post(`/milestone/year-milestone`, firstYear);
export const updateFirstYear = (babyId: any, firstYear: any) =>
  client.patch(`/milestone/${babyId}/year-milestone`, firstYear);

// We are in motion Miletsone
export const getInMotionDataByBaby = (babyId: any) =>
  client.get(`/milestone/${babyId}/in-motion`);
export const addInMotionData = (inMotion: any) =>
  client.post(`/milestone/in-motion`, inMotion);
export const updateInMotionData = (babyId: any, inMotion: any) =>
  client.patch(`/milestone/${babyId}/in-motion`, inMotion);

// My first birthday Milestone
export const getFirstBirthdayData = (babyId: any) =>
  client.get(`/milestone/${babyId}/first-birthday`);
export const addFirstBirthdayData = (firstBirthday: any) =>
  client.post(`/milestone/first-birthday`, firstBirthday);
export const updateFirstBirthdayData = (babyId: any, firstBirthday: any) =>
  client.patch(`/milestone/${babyId}/first-birthday`, firstBirthday);

// First Holiday Milestone
export const getFirstHolidayData = (babyId: any, holidayType: any) =>
  client.get(`/milestone/${babyId}/${holidayType}/first-holiday`);
export const addFirstHolidayData = (firstBirthday: any) =>
  client.post(`/milestone/first-holiday`, firstBirthday);
export const updateFirstHolidayData = (babyId: any, firstBirthday: any) =>
  client.patch(`/milestone/${babyId}/first-holiday`, firstBirthday);

// Teeth Chart Milestone
export const getTeethChartData = (babyId: any) =>
  client.get(`/milestone/${babyId}/teeth-chart`);
export const addTeethChartData = (teethChart: any) =>
  client.post(`/milestone/teeth-chart`, teethChart);
// export const updateTeethChartData = (babyId: any, teethChart: any) =>
//   client.patch(`/milestone/${babyId}/teeth-chart`, teethChart);

// First Words Milestone
export const getFirstWordsByBaby = (babyId: string) =>
  client.get(`/milestone/${babyId}/first-words`);
export const addFirstWord = (firstWord: FirstWordInput) =>
  client.post(`/milestone/first-words`, firstWord);
export const updateFirstWord = (
  babyId: string,
  wordId: string,
  firstWord: FirstWordInput
) => client.patch(`/milestone/${babyId}/first-words/${wordId}`, firstWord);
export const deleteFirstWord = (babyId: string, wordId: string) =>
  client.delete(`/milestone/${babyId}/first-words/${wordId}`);

//Subscriptions
export const createSubscriptionSession = (priceId: any) =>
  client.post(`/payment`, priceId);
export const getUserSubscription = () => client.get(`/payment/subscription`);
export const cancelSubscription = (subscriptionId: string) =>
  client.post(`/payment/${subscriptionId}/cancel`, {});

//Invitations
export const sendInvitation = (invitation: CreateInvitationInterface) =>
  client.post(`/invitation`, invitation);
export const acceptInvitation = (answerInput: any) =>
  client.post(`/invitation/answer`, answerInput);
export const getInvitations = () => client.get(`/Invitation/Receiver`);

//Profile Contributors
export const getBabyProfileContributors = (babyId: string) =>
  client.get(`/baby/${babyId}/BabyUsers`);

export const removeBabyContributor = (babyId: string, userId: string) =>
  client.delete(`/baby/${babyId}/BabyUsers/${userId}`);
